import { graphqlClient } from '@/lib/graphql-client';
import {
  buildGetSkillCategoryOptionsQuery,
  buildGetSkillOptionsQuery,
  buildGetSkillSubCategoryOptionsQuery,
} from '../graphql/skill-lookups';

interface SkillCategoryOption {
  ItemId: string;
  Title: string;
}

interface SkillSubCategoryOption {
  ItemId: string;
  Title: string;
}

/** A skill of the Skill Manager; it belongs to one sub-category. */
export interface SkillOption {
  ItemId: string;
  Name: string;
  SubCategoryId: string;
  /** Key of the skill in the legacy app (seeded skills only), e.g. `csharp`. */
  LegacyKey?: string | null;
}

export interface SkillTaxonomy {
  categories: SkillCategoryOption[];
  subCategories: SkillSubCategoryOption[];
}

const LOOKUP_PAGE_SIZE = 300;

const byTitleAsc = <T extends { Title: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.Title.localeCompare(b.Title));

export const getSkillTaxonomy = async (): Promise<SkillTaxonomy> => {
  const [categories, subCategories] = await Promise.all([
    graphqlClient.query<{ SkillCategorys: { items: SkillCategoryOption[] } }>({
      query: buildGetSkillCategoryOptionsQuery({}, 1, LOOKUP_PAGE_SIZE),
    }),
    graphqlClient.query<{ SkillSubCategorys: { items: SkillSubCategoryOption[] } }>({
      query: buildGetSkillSubCategoryOptionsQuery({}, 1, LOOKUP_PAGE_SIZE),
    }),
  ]);
  return {
    categories: byTitleAsc(categories.SkillCategorys.items),
    subCategories: byTitleAsc(subCategories.SkillSubCategorys.items),
  };
};

export const getSkillsFor = async (
  skillIds: string[],
  subCategoryIds: string[]
): Promise<SkillOption[]> => {
  const clauses = [
    ...(skillIds.length ? [{ ItemId: { $in: skillIds } }] : []),
    ...(subCategoryIds.length ? [{ SubCategoryId: { $in: subCategoryIds } }] : []),
  ];
  if (clauses.length === 0) return [];
  const skills: SkillOption[] = [];
  for (let pageNo = 1; ; pageNo++) {
    const result = await graphqlClient.query<{
      Skills: { hasNextPage: boolean; items: SkillOption[] };
    }>({ query: buildGetSkillOptionsQuery({ $or: clauses }, pageNo, LOOKUP_PAGE_SIZE) });
    skills.push(...result.Skills.items);
    if (!result.Skills.hasNextPage) break;
  }
  return skills.sort((a, b) => a.Name.localeCompare(b.Name));
};

/** The skills a person can pick: those of the sub-categories they recorded experience in. */
export const skillsForSubCategories = (skills: SkillOption[], subCategoryIds: string[]) =>
  skills.filter((skill) => subCategoryIds.includes(skill.SubCategoryId));
